import { jsonStorageStore } from '../jsonStorageStore';
import type {
  EntrPhoto,
  EntrPrintTransport,
  PrintCopiesSummary,
  PrintSelectionInput,
  PrintSelectionResult,
} from '../types';

export interface PrintJobItem {
  photo: EntrPhoto;
  quantity: number;
  printerId: string;
  idempotencyKey: string;
  copyIndex: number;
}

export interface PrintJobSubmission {
  jobKey: string;
}

export interface PrintJobStatusResult {
  status: string;
  isTerminal: boolean;
  error?: string | null;
  copyState?: string | null;
  refundAmountCents?: number | null;
  // final_by of the poll. Undefined: the backend does not send the field.
  finalBy?: string | null;
}

// The payment that a copy carries to the backend. A copy without a price has
// amount 0 and no payment intent: the backend then knows that it has no money
// of that copy.
export interface CopyPayment {
  amountCents: number;
  paymentIntentId: string | null;
  paymentSessionId: string | null;
}

export const FREE_COPY: CopyPayment = {
  amountCents: 0,
  paymentIntentId: null,
  paymentSessionId: null,
};

export interface PrintQueueJob extends CopyPayment {
  sessionId: string;
  wireSessionId: string;
  photoId: string;
  photoType: string;
  imageUuid: string | null;
  printerId: string;
  idempotencyKey: string;
  identityKey: string;
}

// A copy that the backend ended without a print. Its key never prints again,
// thus a retry of the batch skips it.
export interface SettledCopy {
  state: 'not_charged' | 'refunded' | 'refund_failed' | 'failed';
  // The money of the copy that the visitor gets back or does not pay: the
  // refund of a refunded copy, the bound amount of a copy that was not
  // charged, else 0.
  amountCents: number;
}

export interface StoredPrintBatch {
  sessionId: string;
  batchKey: string;
  completedJobs: Record<string, string>;
  settledJobs?: Record<string, SettledCopy>;
  jobIdentities?: string[];
  createdAtMs?: number;
  updatedAtMs?: number;
}

export interface PrintQueueStorage {
  read(): StoredPrintBatch | null;
  write(batch: StoredPrintBatch): void;
  clear(): void;
}

export interface ReliablePrintQueueDeps {
  buildJobs(
    input: PrintSelectionInput,
    batchKey: string
  ): PrintQueueJob[] | null;
  submitPrintJob(
    job: PrintQueueJob,
    signal?: AbortSignal
  ): Promise<PrintJobSubmission>;
  pollPrintJob(
    submission: PrintJobSubmission,
    job: PrintQueueJob,
    signal?: AbortSignal
  ): Promise<PrintJobStatusResult>;
  storage: PrintQueueStorage;
  wait(ms: number, signal?: AbortSignal): Promise<void>;
  observer?: ReliablePrintQueueObserver;
  onPrintRequestError?(error: unknown, job: PrintQueueJob): void;
}

export interface ReliablePrintQueueObserver {
  recordEvent(
    category: string,
    message: string,
    data?: Record<string, unknown>
  ): void;
}

export interface ReliablePrintQueueOptions {
  pollIntervalMs?: number;
  maxPolls?: number;
  pollDeadlineMs?: number;
  preSendPollDeadlineMs?: number;
  batchRecoveryTtlMs?: number;
  now?: () => number;
}

const DEFAULT_PRINT_POLL_INTERVAL_MS = 1_000;
const MAX_PRINT_POLL_INTERVAL_MS = 3_000;
// maxPolls is a per-copy safety cap only. The primary stop is the per-copy
// wall-clock end (see pollEndMs). 400 polls at the longest interval are 20
// min: the pre-send window plus the longest copy deadline and its end.
const DEFAULT_MAX_PRINT_POLLS = 400;
// The backend ends each copy: it prints, or the backend sends it again after a
// confirmed failure, or it cancels the copy at its deadline after its first
// send. Then the other copies of the order end without a send. The poll tells
// the time of that end (final_by), and the kiosk waits for the copy until
// then plus this margin.
export const FINAL_BY_MARGIN_MS = 20_000;
// The safety net for an older backend without final_by. The first send comes
// after the worker queue and a GPU upscale (30-60 s), and the poll gives a
// final state at most 80 s after the 180 s deadline, thus the worst case from
// the submit is about 90 + 180 + 80 s. Exported so a unit test locks the
// timing invariant with the per-copy PRINT_HANG_TIMEOUT_MS of the controller.
export const DEFAULT_POLL_DEADLINE_MS = 360_000;
// The safety net for a copy that has no first send (final_by is null): the
// worker did not take the copy yet.
export const DEFAULT_PRE_SEND_POLL_DEADLINE_MS = 600_000;
const DEFAULT_BATCH_RECOVERY_TTL_MS = 30 * 60 * 1000;
const SETTLED_STATES: ReadonlySet<string> = new Set([
  'not_charged',
  'refunded',
  'refund_failed',
  'failed',
]);

export function createReliablePrintQueue(
  deps: ReliablePrintQueueDeps,
  options: ReliablePrintQueueOptions = {}
): EntrPrintTransport {
  const pollIntervalMs =
    options.pollIntervalMs ?? DEFAULT_PRINT_POLL_INTERVAL_MS;
  const maxPolls = options.maxPolls ?? DEFAULT_MAX_PRINT_POLLS;
  const windows: PollWindows = {
    fixedMs: options.pollDeadlineMs ?? DEFAULT_POLL_DEADLINE_MS,
    preSendMs:
      options.preSendPollDeadlineMs ?? DEFAULT_PRE_SEND_POLL_DEADLINE_MS,
  };
  const batchRecoveryTtlMs =
    options.batchRecoveryTtlMs ?? DEFAULT_BATCH_RECOVERY_TTL_MS;
  const now = options.now ?? (() => Date.now());

  return {
    async printSelection(input): Promise<PrintSelectionResult> {
      let jobs = deps.buildJobs(input, input.idempotencyKey);
      if (!jobs) return noPrintResult();

      let batch = readOrCreateBatch(
        deps.storage,
        input,
        jobs,
        now(),
        batchRecoveryTtlMs
      );
      if (batch.batchKey !== input.idempotencyKey) {
        jobs = deps.buildJobs(input, batch.batchKey);
        if (!jobs) return noPrintResult();
      }
      batch = touchBatch(batch, now());
      deps.storage.write(batch);
      const succeededJobIds = collectSucceededJobIds(jobs, batch);
      const settled: SettledCopy[] = [];

      // The copies print one after the other, and each copy can take
      // minutes, thus each one gets its own poll deadline. A copy that the
      // backend ends without a print does not stop the batch: the next
      // copies still go to the backend, and each copy is printed or settled.
      // A copy that the backend ended at once is final at its first poll,
      // thus it does not wait.
      for (const [index, job] of jobs.entries()) {
        throwIfAborted(input.signal);
        if (batch.completedJobs[job.idempotencyKey]) continue;
        const settledBefore = batch.settledJobs?.[job.idempotencyKey];
        if (settledBefore) {
          settled.push(settledBefore);
          continue;
        }

        if (!job.imageUuid) {
          deps.observer?.recordEvent('entr.print', 'missing print image uuid', {
            photo_id: job.photoId,
            photo_type: job.photoType,
          });
          if (succeededJobIds.length === 0 && settled.length === 0) {
            deps.storage.clear();
          }
          return failure(
            succeededJobIds,
            summarize(
              jobs.length,
              succeededJobIds,
              settled,
              jobs.length - index
            )
          );
        }

        input.onCopyStarted?.();
        const result = await submitAndPoll(
          deps,
          job,
          pollIntervalMs,
          maxPolls,
          windows,
          now,
          input.signal,
          input.onCopyPollEnd
        );
        if (result.kind === 'unsettled') {
          return failure(
            succeededJobIds,
            summarize(
              jobs.length,
              succeededJobIds,
              settled,
              jobs.length - index
            )
          );
        }
        if (result.kind === 'settled') {
          batch = {
            ...batch,
            settledJobs: {
              ...batch.settledJobs,
              [job.idempotencyKey]: result.copy,
            },
          };
          deps.storage.write(touchBatch(batch, now()));
          settled.push(result.copy);
          continue;
        }

        batch = {
          ...batch,
          completedJobs: {
            ...batch.completedJobs,
            [job.idempotencyKey]: result.jobId,
          },
        };
        deps.storage.write(touchBatch(batch, now()));
        succeededJobIds.push(result.jobId);
        await input.onConfirmedCoverage?.(succeededJobIds.length);
      }

      await input.onConfirmedCoverage?.(succeededJobIds.length);
      // Each copy printed or settled: a retry has nothing left to do.
      deps.storage.clear();
      if (settled.length > 0) {
        return failure(
          succeededJobIds,
          summarize(jobs.length, succeededJobIds, settled, 0)
        );
      }
      return {
        ok: true,
        value: {
          printedCount: jobs.length,
          jobId: succeededJobIds.join(','),
        },
      };
    },
  };
}

function summarize(
  total: number,
  succeededJobIds: string[],
  settled: SettledCopy[],
  unsettled: number
): PrintCopiesSummary {
  const refunded = settled.filter((copy) => copy.state === 'refunded');
  const notCharged = settled.filter((copy) => copy.state === 'not_charged');
  return {
    total,
    printed: succeededJobIds.length,
    refunded: refunded.length,
    refundedCents: sumAmounts(refunded),
    notCharged: notCharged.length,
    notChargedCents: sumAmounts(notCharged),
    refundFailed: settled.filter((copy) => copy.state === 'refund_failed')
      .length,
    failed: settled.filter((copy) => copy.state === 'failed').length,
    unsettled,
  };
}

function sumAmounts(copies: SettledCopy[]): number {
  return copies.reduce((sum, copy) => sum + copy.amountCents, 0);
}

export function createStoragePrintQueueStore(
  storage: Storage | undefined,
  key: string
): PrintQueueStorage {
  return jsonStorageStore<StoredPrintBatch>(storage, key, (parsed) => {
    if (
      typeof parsed.sessionId !== 'string' ||
      typeof parsed.batchKey !== 'string' ||
      !parsed.completedJobs ||
      typeof parsed.completedJobs !== 'object'
    ) {
      return null;
    }
    return {
      sessionId: parsed.sessionId,
      batchKey: parsed.batchKey,
      completedJobs: sanitizeCompletedJobs(parsed.completedJobs),
      settledJobs: sanitizeSettledJobs(parsed.settledJobs),
      jobIdentities: sanitizeJobIdentities(parsed.jobIdentities),
      createdAtMs: sanitizeTimestamp(parsed.createdAtMs),
      updatedAtMs: sanitizeTimestamp(parsed.updatedAtMs),
    };
  });
}

export function toPrintQueueJob(
  job: PrintJobItem,
  sessionId: string,
  wireSessionId: string = sessionId,
  payment: CopyPayment = FREE_COPY
): PrintQueueJob {
  return {
    sessionId,
    wireSessionId,
    photoId: job.photo.id,
    photoType: job.photo.type,
    imageUuid: job.photo.printImageUuid ?? null,
    printerId: job.printerId,
    idempotencyKey: job.idempotencyKey,
    identityKey: createPrintJobIdentityKey(job, sessionId),
    amountCents: payment.amountCents,
    paymentIntentId: payment.paymentIntentId,
    paymentSessionId: payment.paymentSessionId,
  };
}

function readOrCreateBatch(
  storage: PrintQueueStorage,
  input: PrintSelectionInput,
  jobs: PrintQueueJob[],
  nowMs: number,
  batchRecoveryTtlMs: number
): StoredPrintBatch {
  const stored = storage.read();
  const jobIdentities = jobs.map((job) => job.identityKey);
  if (
    stored?.sessionId === input.sessionId &&
    stored.batchKey === input.idempotencyKey
  ) {
    return {
      ...stored,
      jobIdentities: stored.jobIdentities ?? jobIdentities,
      createdAtMs: stored.createdAtMs ?? nowMs,
      updatedAtMs: stored.updatedAtMs ?? nowMs,
    };
  }

  if (
    stored?.sessionId === input.sessionId &&
    stored.jobIdentities &&
    sameStringArray(stored.jobIdentities, jobIdentities) &&
    isRecoverableBatch(stored, nowMs, batchRecoveryTtlMs)
  ) {
    return stored;
  }

  return {
    sessionId: input.sessionId,
    batchKey: input.idempotencyKey,
    completedJobs: {},
    jobIdentities,
    createdAtMs: nowMs,
    updatedAtMs: nowMs,
  };
}

function touchBatch(batch: StoredPrintBatch, nowMs: number): StoredPrintBatch {
  return {
    ...batch,
    createdAtMs: batch.createdAtMs ?? nowMs,
    updatedAtMs: nowMs,
  };
}

function isRecoverableBatch(
  batch: StoredPrintBatch,
  nowMs: number,
  batchRecoveryTtlMs: number
): boolean {
  if (typeof batch.updatedAtMs !== 'number') return false;
  return nowMs - batch.updatedAtMs <= batchRecoveryTtlMs;
}

function collectSucceededJobIds(
  jobs: PrintQueueJob[],
  batch: StoredPrintBatch
): string[] {
  return jobs
    .map((job) => batch.completedJobs[job.idempotencyKey])
    .filter((jobId): jobId is string => typeof jobId === 'string');
}

type CopyResult =
  | { kind: 'printed'; jobId: string }
  | { kind: 'settled'; copy: SettledCopy }
  | { kind: 'unsettled' };

interface PollWindows {
  // Without final_by (an older backend), from the start of the poll.
  fixedMs: number;
  // Before the first send of the copy (final_by is null), from the start of
  // the poll.
  preSendMs: number;
}

/**
 * The time until which the kiosk polls a copy that is not final. The end
 * time of the backend decides when the poll gives it. The two windows are
 * only a safety net: for an older backend, and for a copy before its first
 * send.
 */
export function pollEndMs(
  status: Pick<PrintJobStatusResult, 'finalBy'>,
  startedAtMs: number,
  windows: PollWindows
): number {
  if (status.finalBy === null) return startedAtMs + windows.preSendMs;
  const finalByMs =
    typeof status.finalBy === 'string' ? Date.parse(status.finalBy) : NaN;
  return Number.isFinite(finalByMs)
    ? finalByMs + FINAL_BY_MARGIN_MS
    : startedAtMs + windows.fixedMs;
}

async function submitAndPoll(
  deps: ReliablePrintQueueDeps,
  job: PrintQueueJob,
  pollIntervalMs: number,
  maxPolls: number,
  windows: PollWindows,
  now: () => number,
  signal?: AbortSignal,
  onPollEnd?: (pollEndsAtMs: number) => void
): Promise<CopyResult> {
  const startedAtMs = now();
  const submission = await tryRequest(deps, job, signal, () =>
    deps.submitPrintJob(job, signal)
  );
  if (!submission) return { kind: 'unsettled' };

  for (let attempt = 0; attempt <= maxPolls; attempt += 1) {
    const status = await tryRequest(deps, job, signal, () =>
      deps.pollPrintJob(submission, job, signal)
    );
    if (!status) return { kind: 'unsettled' };

    if (status.isTerminal) {
      if (status.status === 'completed') {
        return { kind: 'printed', jobId: submission.jobKey };
      }
      deps.observer?.recordEvent('entr.print', 'print job terminal failure', {
        // Full image UUID is intentional telemetry metadata for print-job joins.
        image_uuid: job.imageUuid,
        status: status.status,
        copy_state: status.copyState ?? null,
        error: status.error ?? null,
      });
      return { kind: 'settled', copy: settledCopy(status, job) };
    }

    const pollEndsAtMs = pollEndMs(status, startedAtMs, windows);
    onPollEnd?.(pollEndsAtMs);
    if (now() >= pollEndsAtMs || attempt >= maxPolls) break;
    await deps.wait(printPollDelayMs(pollIntervalMs, attempt), signal);
  }

  deps.observer?.recordEvent('entr.print', 'print polling timed out', {
    // Full image UUID is intentional telemetry metadata for print-job joins.
    image_uuid: job.imageUuid,
    max_polls: maxPolls,
  });
  return { kind: 'unsettled' };
}

// A final answer without a print. A backend without copy_state tells only
// "failed": that copy has no known refund. The backend gives no amount for a
// copy that it did not charge, thus the amount comes from the copy binding.
function settledCopy(
  status: PrintJobStatusResult,
  job: PrintQueueJob
): SettledCopy {
  const state =
    status.copyState && SETTLED_STATES.has(status.copyState)
      ? (status.copyState as SettledCopy['state'])
      : 'failed';
  if (state === 'refunded') {
    return { state, amountCents: status.refundAmountCents ?? 0 };
  }
  if (state === 'not_charged') return { state, amountCents: job.amountCents };
  return { state, amountCents: 0 };
}

function printPollDelayMs(baseIntervalMs: number, attempt: number): number {
  return Math.min(baseIntervalMs * 2 ** attempt, MAX_PRINT_POLL_INTERVAL_MS);
}

async function tryRequest<T>(
  deps: ReliablePrintQueueDeps,
  job: PrintQueueJob,
  signal: AbortSignal | undefined,
  run: () => Promise<T>
): Promise<T | null> {
  try {
    throwIfAborted(signal);
    return await run();
  } catch (error) {
    deps.onPrintRequestError?.(error, job);
    return null;
  }
}

function throwIfAborted(signal?: AbortSignal): void {
  if (!signal?.aborted) return;
  if (signal.reason instanceof Error) throw signal.reason;
  if (typeof DOMException === 'function') {
    throw new DOMException('Print selection aborted', 'AbortError');
  }
  throw Object.assign(new Error('Print selection aborted'), {
    name: 'AbortError',
  });
}

// Without a message, the result has no message key (not a key with the value
// undefined).
export function noPrintResult(message?: string): PrintSelectionResult {
  return {
    ok: false,
    reason: 'network-error',
    ...(message === undefined ? {} : { message }),
    printedCount: 0,
    succeededJobIds: [],
  };
}

function failure(
  succeededJobIds: string[],
  copies: PrintCopiesSummary
): PrintSelectionResult {
  return {
    ok: false,
    reason: 'network-error',
    printedCount: succeededJobIds.length,
    succeededJobIds,
    copies,
  };
}

function sanitizeCompletedJobs(input: object): Record<string, string> {
  return Object.fromEntries(
    Object.entries(input).filter(
      (entry): entry is [string, string] => typeof entry[1] === 'string'
    )
  );
}

// A batch that the previous build stored keeps the amount in refundCents. A
// settled copy that the read drops would be submitted again on a retry.
function sanitizeSettledJobs(
  input: unknown
): Record<string, SettledCopy> | undefined {
  if (!input || typeof input !== 'object') return undefined;
  const entries = Object.entries(input).flatMap(
    ([key, value]): Array<[string, SettledCopy]> => {
      const copy = value as
        | (Partial<SettledCopy> & { refundCents?: unknown })
        | null;
      if (!copy || typeof copy !== 'object') return [];
      const amountCents = copy.amountCents ?? copy.refundCents;
      if (
        typeof copy.state !== 'string' ||
        !SETTLED_STATES.has(copy.state) ||
        typeof amountCents !== 'number' ||
        !Number.isFinite(amountCents)
      ) {
        return [];
      }
      return [
        [key, { state: copy.state as SettledCopy['state'], amountCents }],
      ];
    }
  );
  return entries.length > 0 ? Object.fromEntries(entries) : undefined;
}

function sanitizeJobIdentities(input: unknown): string[] | undefined {
  if (!Array.isArray(input)) return undefined;
  const identities = input.filter(
    (entry): entry is string => typeof entry === 'string'
  );
  return identities.length > 0 ? identities : undefined;
}

function sanitizeTimestamp(input: unknown): number | undefined {
  return typeof input === 'number' && Number.isFinite(input)
    ? input
    : undefined;
}

function sameStringArray(left: string[], right: string[]): boolean {
  return (
    left.length === right.length &&
    left.every((value, index) => value === right[index])
  );
}

function createPrintJobIdentityKey(
  job: PrintJobItem,
  sessionId: string
): string {
  return [
    sessionId,
    job.photo.type,
    job.photo.id,
    job.printerId,
    String(job.copyIndex),
  ].join(':');
}
